import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { PushNotifications } from '@capacitor/push-notifications';
import { from, switchMap } from 'rxjs';
import { User } from '../models/user';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';

@Injectable({
    providedIn: 'root',
})
export class NotificationService {
    private deviceToken: string | null = null;
    private user: User | undefined = undefined;

    constructor(
        private readonly apiService: ApiService,
        private readonly authService: AuthService,
    ) {}

    async init(): Promise<void> {
        this.authService.userLoggedIn.subscribe((user) => {
            this.user = user ?? undefined;
            if (user && this.deviceToken) {
                this.addDeviceTokenToUser(user, this.deviceToken);
            }
        });

        this.authService.logoutRequested.subscribe(() => {
            this.removeDeviceTokenFromBackend();
        });

        await PushNotifications.addListener('registration', (token) => {
            this.deviceToken = token.value;
            if (this.user && this.deviceToken) {
                this.addDeviceTokenToUser(this.user, this.deviceToken);
            }
        });

        await PushNotifications.addListener('registrationError', (err) => {
            console.error('Push registration error:', err);
        });

        await PushNotifications.addListener('pushNotificationReceived', (notification) => {
            // App in foreground; notification payload available for display
            console.debug('Push received (foreground):', notification);
        });

        await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
            // User tapped notification; action.notification contains payload
            console.debug('Push action performed:', action);
        });
    }

    addDeviceTokenToUser(user: User, token: string): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            from(Device.getInfo())
                .pipe(
                    switchMap((info) =>
                        this.apiService.post('/api/users/' + user.id + '/token', {
                            token,
                            name: info?.name ?? 'Unknown',
                        }),
                    ),
                )
                .subscribe({
                    next: () => resolve(),
                    error: (err) => reject(err),
                });
        });
    }

    private removeDeviceTokenFromBackend(): void {
        if (!this.deviceToken) {
            return;
        }
        const url = '/api/users/device-token?token=' + encodeURIComponent(this.deviceToken);
        this.apiService.delete(url).subscribe({
            error: (err) => {
                console.warn('Failed to remove device token on logout:', err);
            },
        });
    }

    async register(): Promise<void> {
        if (!Capacitor.isNativePlatform()) {
            return;
        }
        let permStatus = await PushNotifications.checkPermissions();
        if (permStatus.receive === 'prompt') {
            permStatus = await PushNotifications.requestPermissions();
        }
        if (permStatus.receive !== 'granted') {
            return;
        }
        await PushNotifications.register();
    }
}
